import { Injectable } from '@angular/core';
import { FormGroup } from "@angular/forms";

@Injectable({
  providedIn: 'root'
})
export class SaveSearchService {

  constructor() { }

  private static prefix = "saveForm_";
  private static rawPrefix = "saveRaw_";

  /**
   * Writes the contents of a FormGroup and optional extra data to window.localStorage
   * @param id key to retrieve data later
   * @param form FormGroup to be serialized
   * @param addendum Any data besides the FormGroup that needs to be preserved.
   */
  public saveForm(id: string, form: FormGroup, addendum?) {
    console.log("LocalStorage-----Saving form " + id);
    let formValues = {};
    Object.keys(form.controls).forEach((key: string) => {

      //special handling for data types that JSON cannot encode
      if (form.get(key).value instanceof Date) {
        let dateObject = {
          ndmsParamType: 'Date',
          value: form.get(key).value
        }

        formValues[key] = dateObject;
      } else {
        const control = form.get(key);
        formValues[key] = control.value;
      }
    });

    const saveObject = {
      ignore: false,
      formValues: formValues,
      addendum: addendum
    }

    window.localStorage.setItem(SaveSearchService.prefix + id, JSON.stringify(saveObject));
  }

  /**
   * Retrieves previously stored data from window.localStorage
   * @param id Key for target data
   * @param form FormGroup object which will be updated with retrieved data
   * @param addendum Object which will be updated with retrieved non-FormGroup data
   * @returns Success or failure
   */
  public restoreForm(id: string, form: FormGroup, addendum?): boolean {
    try {
      const retrieved = JSON.parse(window.localStorage.getItem(SaveSearchService.prefix + id));

      if (!retrieved) {
        console.log("LocalStorage-----Restoring form " + id + " (not found)");
        console.log(retrieved);
        //No saved data found
        return false;
      }

      if (retrieved["ignore"]) {
        console.log("LocalStorage-----Restoring form " + id + " (ignored)");
        //Data flagged to ignore (e.g. user cleared search)
        return false;
      }

      //Repopulate provided formGroup with retrieved control values
      const retrievedForm = retrieved["formValues"];
      Object.keys(retrievedForm).forEach((key: string) => {
        let data = retrievedForm[key];

        //Sepcial hendling for data types that JSON cannot encode
        if (data?.ndmsParamType) {
          let ndmsParamType = data.ndmsParamType;
          let result = null;

          switch (ndmsParamType) {
            case 'Date':
              result = Date.parse(data.value);
              result = new Date(data.value);
              break;
          }

          if (result) form.get(key).setValue(result);

        } else {
          if (data) form.get(key).setValue(data);
        }
        
      })

      //Repopulate provided addendum object with retrieved properties
      if (retrieved["addendum"]) {
        let rAddendum = retrieved["addendum"];
        Object.keys(rAddendum).forEach((key: string) => {
          addendum[key] = rAddendum[key];
        })
      }

      //Retrieval succeeded
      console.log("LocalStorage-----Restoring form " + id);
      return true;
    } catch (error) {
      //Retrieval failed
      console.error(error);
      console.log("LocalStorage-----Restoring form " + id + " (failed)");
      return false;
    }
  }

  /**
   * Writes the contents of a javascript object to window.localStorage - no FormGroup
   * @param id key to retrieve data later
   * @param data Data to save.
   */
  public saveRaw(id: string, data) {
    console.log("LocalStorage-----Saving raw: " + id);
    const saveObject = {
      ignore: false,
      data: data
    }
    window.localStorage.setItem(SaveSearchService.rawPrefix + id, JSON.stringify(saveObject));
  }

  /**
   * Retrieves previously stored data from window.localStorage
   * @param id Key for target data
   * @param data Object which will be updated with retrieved data
   * @returns Success or failure
   */
  public restoreRaw(id: string, data): boolean {
    try {
      const retrieved = JSON.parse(window.localStorage.getItem(SaveSearchService.rawPrefix + id));

      if (!retrieved) {
        console.log("LocalStorage-----Restoring raw: " + id + " (not found)");
        console.log(retrieved);
        //No saved data found
        return false;
      }

      if (retrieved["ignore"]) {
        console.log("LocalStorage-----Restoring raw: " + id + " (ignored)");
        //Data flagged to ignore (e.g. user cleared search)
        return false;
      }

      //Repopulate provided addendum object with retrieved properties
      if (retrieved["data"]) {
        let rData = retrieved["data"];
        Object.keys(rData).forEach((key: string) => {
          data[key] = rData[key];
        })
      }

      //Retrieval succeeded
      console.log("LocalStorage-----Restoring raw: " + id);
      return true;
    } catch (error) {
      //Retrieval failed
      console.error(error);
      console.log("LocalStorage-----Restoring raw: " + id + " (failed)");
      return false;
    }
  }

  /**
   * Flags existing form to be ignored. Prevents loading without erasing from memory.
   * @param id Key for target data
   */
  public clearSavedForm(id: string) {
    console.log("LocalStorage-----Clearing form " + id);
    const retrieved = JSON.parse(window.localStorage.getItem(SaveSearchService.prefix + id));
    if (retrieved) {
      retrieved["ignore"] = true;
      window.localStorage.setItem(SaveSearchService.prefix + id, JSON.stringify(retrieved));
    }
  }

  /**
   * Flags existing raw data to be ignored. Prevents loading without erasing from memory.
   * @param id Key for target data
   */
  public clearSavedRaw(id: string) {
    console.log("LocalStorage-----Clearing raw: " + id);
    const retrieved = JSON.parse(window.localStorage.getItem(SaveSearchService.rawPrefix + id));
    if (retrieved) {
      retrieved["ignore"] = true;
      window.localStorage.setItem(SaveSearchService.rawPrefix + id, JSON.stringify(retrieved));
    }
  }
}
